import { type ClassValue, clsx } from "clsx";
import { twMerge } from "tailwind-merge";

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

export function formatIban(iban: string): string {
  return iban
    .replace(/\s/g, "")
    .replace(/(.{4})/g, "$1 ")
    .trim();
}

/**
 * Serialize a `yyyy-MM-dd` calendar date (birthdate, incorporation date) as a
 * fixed UTC-midnight instant. Unlike the timestamp-filter convention, this must
 * NOT map through the local timezone: the UTC date part has to equal the picked
 * day in every zone, or e.g. a Berlin user's "24 Jul" would persist as "23 Jul".
 */
export function calendarDateToUtcInstant(date: string): string {
  return `${date}T00:00:00.000Z`;
}

export function shortenAddress(addr: string): string {
  if (addr.length <= 10) return addr;
  const hasPrefix = addr.startsWith("0x");
  const start = hasPrefix ? 6 : 4;
  return `${addr.slice(0, start)}…${addr.slice(-4)}`;
}

export function approvedSourcesLabel(count: number, noun: string): string {
  return `${count} approved ${noun}${count !== 1 ? "s" : ""}`;
}

/** Format a numeric amount as currency (2 decimals). Falls back to a plain fixed number when no currency is given. */
export function formatCurrencyAmount(amount: number, currency?: string): string {
  if (!currency) return amount.toFixed(2);
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency,
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(amount);
}

export function formatCurrencies(currencies: readonly string[]): string {
  return [...currencies].sort().join(", ");
}

const statusLabelOverrides: Record<string, string> = {
  allunity: "AllUnity",
  pendingreview: "Pending Review",
  pthredemption: "PTH Redemption",
  automaticpthredemption: "Automatic PTH Redemption",
};

export function formatStatusLabel(status: string): string {
  const key = status.toLowerCase().replace(/[_\s]/g, "");
  if (statusLabelOverrides[key]) return statusLabelOverrides[key];
  return status
    .replace(/([a-z])([A-Z])/g, "$1 $2")
    .replace(/_/g, " ")
    .toLowerCase()
    .replace(/\b\w/g, (c) => c.toUpperCase());
}
